import React, { FC, useContext, useMemo } from 'react';

import { Descendant } from 'slate';

import { SearchableSelectType } from 'components/inputs/searchableSelect';
import styled from 'styled-components';

import aampeTheme from '../../../constants/aampeTheme';

import { EditorContext } from 'context/EditorContext';

import useComponentTypesQuery from 'hooks/component-types/useComponentTypesQuery';
import useDatasetByIdQuery from 'hooks/datasets/useDatasetByIdQuery';

import { ComponentTypeTransformed } from 'models/component-types-model';
import { DatasetModel } from 'models/dataset.model';
import { SectionElement } from 'models/editor.model';
import { FormulaBaseModel } from 'models/formula.model';

import MapElement from './MapElement';

interface MapElementListsProps {
    mapOptions: SearchableSelectType[];
    selectedMessageForPreview?: FormulaBaseModel;
    showMappingErrors?: boolean;
}

const MapElementList: FC<MapElementListsProps> = ({
    mapOptions,
    selectedMessageForPreview,
    showMappingErrors,
}) => {
    const {
        editorDetails: { formula },
    } = useContext(EditorContext);

    const { data: componentTypes } = useComponentTypesQuery();

    const { data: datasetData } = useDatasetByIdQuery(formula?.dataset_id!);

    const structure = useMemo<Descendant[]>(() => {
        return selectedMessageForPreview?.structure || formula?.structure || [];
    }, [formula, selectedMessageForPreview]);

    return (
        <Container>
            <Heading>Map Elements</Heading>
            <ScrollableList>
                {structure.map((element, index) => {
                    return (
                        <MapElement
                            key={index}
                            element={element as SectionElement}
                            componentTypes={
                                componentTypes as ComponentTypeTransformed
                            }
                            datasetData={datasetData as DatasetModel}
                            mapOptions={mapOptions}
                            showMappingErrors={showMappingErrors}
                        />
                    );
                })}
            </ScrollableList>
        </Container>
    );
};

const Container = styled.div({
    display: 'flex',
    flexDirection: `column`,
    gap: aampeTheme.spacing(2),
    alignItems: 'start',
    height: '60%',
});
const ScrollableList = styled.div({
    backgroundColor: aampeTheme.colors.backgroundWG02,
    display: 'flex',
    flexDirection: 'column',
    gap: aampeTheme.spacing(2),
    width: '100%',
    padding: aampeTheme.spacing4(3),
});

const Heading = styled.div({
    color: aampeTheme.colors.black,
    fontSize: aampeTheme.size.font.default,
    fontWeight: aampeTheme.weights.extraHeavy,
    lineHeight: aampeTheme.size.lineHeight.default,
});

export default MapElementList;
